import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  intelligenceQAApi,
  type ConflictEntry,
  type ConsolidatedRecommendation,
  type GovernanceDecision,
  type IQADashboardSnapshot,
  type IQALearningUpdate,
  type IQATimelineEvent,
  type QAQueueItem,
  type QualityScoreBreakdown,
  type QualityTrend,
  type SafetyAssessment,
} from '../../api/intelligence-qa-iqa001-runtime';

type Tab = 'dashboard' | 'queue' | 'conflicts' | 'consolidation' | 'decisions' | 'trends' | 'learning' | 'timeline';

export function IntelligenceQAPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<IQADashboardSnapshot | null>(null);
  const [queue, setQueue] = useState<QAQueueItem[]>([]);
  const [conflicts, setConflicts] = useState<ConflictEntry[]>([]);
  const [consolidated, setConsolidated] = useState<ConsolidatedRecommendation[]>([]);
  const [decisions, setDecisions] = useState<GovernanceDecision[]>([]);
  const [trends, setTrends] = useState<QualityTrend[]>([]);
  const [learning, setLearning] = useState<IQALearningUpdate[]>([]);
  const [timeline, setTimeline] = useState<IQATimelineEvent[]>([]);
  const [selected, setSelected] = useState<QAQueueItem | null>(null);
  const [quality, setQuality] = useState<QualityScoreBreakdown | null>(null);
  const [safety, setSafety] = useState<SafetyAssessment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-rose-200 bg-white dark:border-rose-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-rose-700 dark:text-rose-300';
  const shell = 'bg-rose-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, q, conf, cons, dec, tr, learn, tl] = await Promise.all([
        intelligenceQAApi.dashboard(), intelligenceQAApi.queue(),
        intelligenceQAApi.conflicts(), intelligenceQAApi.consolidation(),
        intelligenceQAApi.decisions(), intelligenceQAApi.trends(),
        intelligenceQAApi.learning(), intelligenceQAApi.history(),
      ]);
      setDashboard(dash); setQueue(q); setConflicts(conf); setConsolidated(cons);
      setDecisions(dec); setTrends(tr); setLearning(learn); setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Intelligence QA');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const inspect = async (item: QAQueueItem) => {
    setSelected(item);
    try {
      const [q, s] = await Promise.all([intelligenceQAApi.quality(item.id), intelligenceQAApi.safety(item.id)]);
      setQuality(q); setSafety(s); setTab('queue');
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to inspect recommendation'); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Overview' }, { id: 'queue', label: 'QA Queue' },
    { id: 'conflicts', label: 'Conflicts' }, { id: 'consolidation', label: 'Consolidated' },
    { id: 'decisions', label: 'Decisions' }, { id: 'trends', label: 'Trends' },
    { id: 'learning', label: 'Learning' }, { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>IQA-000001 · Intelligence Quality Assurance</p>
            <h1 className="text-xl font-semibold">Intelligence QA Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Quality <strong className={accent}>{dashboard.overall_quality_score}</strong></span>
                <span>Conflicts <strong className={accent}>{dashboard.conflict_count}</strong></span>
                <span>Evidence <strong className={accent}>{dashboard.evidence_coverage_pct}%</strong></span>
                <span>Approval <strong className={accent}>{dashboard.approval_rate_pct}%</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/recommendation-validation" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Validation</Link>
            <Link to="/platform/intelligence-qa-iqa001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading Intelligence QA…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-rose-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Quality Score', value: dashboard.overall_quality_score },
            { label: 'Pending Review', value: dashboard.pending_review },
            { label: 'Blocked by Safety', value: dashboard.blocked_by_safety },
            { label: 'Quality Trend', value: dashboard.quality_trend },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'queue' && (
        <div className="space-y-4">
          {queue.map((item) => (
            <button key={item.id} type="button" onClick={() => inspect(item)} className={`block w-full rounded-xl border p-4 text-left ${card} ${selected?.id === item.id ? 'ring-2 ring-rose-400' : ''}`}>
              <div className="flex justify-between">
                <p className="font-semibold">{item.title}</p>
                <span className={`text-sm uppercase ${item.status === 'blocked' ? 'text-red-600' : item.status === 'approved' ? 'text-rose-600' : muted}`}>{item.status}</span>
              </div>
              <p className={`text-xs ${muted}`}>{item.source} · Quality {Math.round(item.quality_score * 100)}% · Safety {Math.round(item.safety_score * 100)}%{item.conflict_flag ? ' · Conflict' : ''}</p>
            </button>
          ))}
          {selected && quality && safety && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">Scores — {selected.title}</h3>
              <p className={`mt-2 text-sm ${muted}`}>Overall {Math.round(quality.overall_quality_score * 100)}% · Evidence {Math.round(quality.evidence_score * 100)}% · Explainability {Math.round(quality.explainability_score * 100)}%</p>
              <p className={`text-sm ${safety.passed ? 'text-rose-600' : 'text-red-600'}`}>{safety.passed ? 'Safety passed' : `Blocked: ${safety.blocked_reason}`}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'conflicts' && conflicts.map((c) => (
        <div key={c.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{c.title}</p><span className="text-xs uppercase">{c.severity}</span></div>
          <p className={`text-xs ${muted}`}>{c.type} · {c.status}{c.resolution ? ` · ${c.resolution}` : ''}</p>
        </div>
      ))}

      {tab === 'consolidation' && consolidated.map((c) => (
        <div key={c.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{c.title}</p>
          <p className={`text-sm ${accent}`}>{c.expected_impact} · Quality {Math.round(c.quality_score * 100)}%</p>
          <p className={`text-xs ${muted}`}>Merged {c.merged_from.length} recs from {c.sources.join(', ')}</p>
        </div>
      ))}

      {tab === 'decisions' && decisions.map((d) => (
        <div key={d.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold capitalize">{d.action}</p><span className={`text-xs ${muted}`}>{d.actor}</span></div>
          {d.reason && <p className={`text-sm ${muted}`}>{d.reason}</p>}
        </div>
      ))}

      {tab === 'trends' && trends.map((t) => (
        <div key={t.period} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{t.period}</p>
          <p className={`text-sm ${accent}`}>Quality {t.quality_score} · Conflicts {t.conflict_rate}% · Evidence {t.evidence_coverage}%</p>
        </div>
      ))}

      {tab === 'learning' && learning.map((u) => (
        <div key={u.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{u.action}</p>
          <p className={`text-xs ${muted}`}>{u.target} · {u.impact}</p>
        </div>
      ))}

      {tab === 'timeline' && timeline.map((e) => (
        <div key={e.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{e.title}</p>
          <p className={`text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}
